import {
    Loader,
    Celebration,
    IconEdit,
    IconEye,
    Text,
    Title
} from "dash-styled"
import React, { FC, useCallback, useEffect, useState } from "react"
import { routePaths } from "../../../../../../Routes"
import {
    AppDetailBase,
    AppDetailBaseProps
} from "../AppDetailBase/AppDetailBase"
import { ReactComponent as IconUpload } from "icons/lib/upload.svg"
import { useJobIdToReview } from "../../../../../../Hooks/useJobIdToReview"
import { useCanLabelApp } from "../../../../../../Hooks/useCanLabelApp"
import { useJobTotalCount } from "../../../../../../Hooks/useJobTotalCount"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import { Link } from "resha"
import { useJobCount } from "../../../../../../Hooks/useJobCount"
import { NavButton } from "../../../../../Common/NavButton/NavButton"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { selectHasFreemiumExpired } from "../../../../../../Helpers/Functions/Selectors/auth"
import { AppInstanceStatus } from "../../../../../../Models/API/AppInstanceModel"
import { Redirect } from "react-router"

interface WelcomeProps {
    orgUsername: string
    appID: string
    onboardingCompleted?: boolean
}

export const AppWelcome: FC<WelcomeProps> = ({
    orgUsername,
    appID,
    onboardingCompleted
}) => {
    const [showCelebration, setShowCelebration] = useState(
        !!onboardingCompleted
    )

    const runFn = useCallback((callFn: () => void) => {
        setTimeout(() => callFn(), 500)
    }, [])

    const instanceFetch = useAppInstance(appID)
    const totalJobs = useJobTotalCount(appID)
    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)
    const rolesFetch = useAssumedRoles(appID)
    const roles = getValue(rolesFetch) ?? []

    const { canLabel, couldLabel, labelableJobCount } = useCanLabelApp(
        appID,
        false
    )
    const jobToReview = useJobIdToReview(appID)

    const jobCount = useJobCount(appID)

    useEffect(() => {
        if (showCelebration) {
            const timeout = setTimeout(() => setShowCelebration(false), 5000)
            return () => clearTimeout(timeout)
        }
    }, [setShowCelebration, showCelebration])

    const containerProps: AppDetailBaseProps = {
        orgUsername,
        id: appID,
        title: "",
        breadcrumbs: [],
        fixedContentWidth: false,
        hideBanner: true
    }

    if (
        !hasValue(instanceFetch) ||
        jobCount === undefined ||
        !hasValue(jobToReview)
    ) {
        return (
            <AppDetailBase {...containerProps}>
                <Loader />
            </AppDetailBase>
        )
    }

    const reviewingJobs = getValue(jobToReview)

    if (
        instanceFetch.value.status === AppInstanceStatus.DISABLED ||
        (jobCount.COMPLETED > 0 &&
            (reviewingJobs === "nothing_to_review" ||
                jobCount.COMPLETED !== reviewingJobs?.total)) ||
        hasFreemiumPlanExpired
    ) {
        return <Redirect to={routePaths.appDetail(orgUsername, appID)} />
    }

    const hasAvailableAction =
        couldLabel ||
        hasAppPermission(roles, "job:submit") ||
        hasAppPermission(roles, "job:review")

    return (
        <AppDetailBase {...containerProps} breadcrumbs={["Welcome"]}>
            <div
                className="w-full h-full relative flex flex-col justify-center items-center pb-20"
                data-testid="welcome-page"
            >
                <Title
                    color="darkviolet"
                    variant="heading1"
                    component="h2"
                    className="text-center"
                    weight="semibold"
                >
                    {onboardingCompleted ? (
                        <>
                            You processed your first task!
                            <br />
                            What’s next?
                        </>
                    ) : (
                        `Welcome! ${hasAvailableAction ? "What's next?" : ""}`
                    )}
                </Title>
                <div
                    className={`flex items-center justify-center mt-10 ${
                        onboardingCompleted ? "z-400" : ""
                    }`}
                >
                    {hasAppPermission(roles, "job:submit") && (
                        <NavButton
                            className="h-56 w-46 rounded-lg mr-4 bg-rhino-100 border-rhino-300"
                            active={true}
                            color="white"
                            key="upload"
                            to={routePaths.appAddData(
                                orgUsername,
                                appID,
                                "input"
                            )}
                            testId="welcome-upload"
                        >
                            <div className="flex flex-col justify-center items-center">
                                <IconUpload className="mb-2 w-14 h-14" />
                                <Text
                                    component="span"
                                    size="xl"
                                    className="mt-4"
                                    color="currentColor"
                                >
                                    Upload data
                                </Text>
                                <Text
                                    size="sm"
                                    component="span"
                                    color="gray"
                                    className="mt-6 px-4"
                                >
                                    {`${totalJobs ?? 0} uploaded already`}
                                </Text>
                            </div>
                        </NavButton>
                    )}
                    {couldLabel && (
                        <NavButton
                            className="h-56 w-46 rounded-lg mr-4 bg-rhino-100 border-rhino-300"
                            active={true}
                            color="white"
                            key="label"
                            to={routePaths.appDetailLabeling(
                                orgUsername,
                                appID
                            )}
                            testId="welcome-label"
                            disabled={!canLabel}
                            disabledClassName="text-gray-500"
                        >
                            <div className="flex flex-col justify-center items-center">
                                <IconEdit className={`mb-2`} size="xxxxxl" />
                                <Text
                                    component="span"
                                    size="xl"
                                    className="mt-4"
                                    color="currentColor"
                                >
                                    {onboardingCompleted
                                        ? "Continue processing"
                                        : "Process myself"}
                                </Text>
                                <Text
                                    size="sm"
                                    component="span"
                                    color="gray"
                                    className="mt-6 px-4"
                                >
                                    {`${labelableJobCount} to be completed`}
                                </Text>
                            </div>
                        </NavButton>
                    )}

                    {hasAppPermission(roles, "job:review") && (
                        <NavButton
                            className="h-56 w-46 rounded-lg bg-rhino-100 border-rhino-300"
                            active={true}
                            color="white"
                            key="review"
                            to={routePaths.appDetailReviewJobs(
                                orgUsername,
                                appID
                            )}
                            testId="welcome-review"
                            disabled={
                                reviewingJobs === "nothing_to_review" ||
                                reviewingJobs === undefined ||
                                reviewingJobs.total === 0
                            }
                            disabledClassName="text-gray-500"
                        >
                            <div className="flex flex-col justify-center items-center">
                                <IconEye className={`mb-2`} size="xxxxxl" />
                                <Text
                                    component="span"
                                    size="xl"
                                    className="mt-4"
                                    color="currentColor"
                                >
                                    Review results
                                </Text>
                                <Text
                                    size="sm"
                                    component="span"
                                    color="gray"
                                    className="mt-6 px-4"
                                >
                                    {`${
                                        reviewingJobs !== "nothing_to_review"
                                            ? reviewingJobs?.total ?? 0
                                            : 0
                                    } waiting for review`}
                                </Text>
                            </div>
                        </NavButton>
                    )}
                </div>
                {hasAppPermission(roles, "collaborators:invite") && (
                    <div className="mt-10">
                        Or{" "}
                        <Link
                            to={routePaths.appCollaborators(
                                orgUsername,
                                appID,
                                undefined,
                                "invite"
                            )}
                        >
                            invite people to help out
                        </Link>
                    </div>
                )}
                {showCelebration && (
                    <Celebration
                        runFn={runFn}
                        data-testid="welcome-celebration"
                    />
                )}
            </div>
        </AppDetailBase>
    )
}
